import { Box, Typography, Chip } from "@mui/material";
import { createBlobInContainer } from "./azureBlob";

// Payout Format Utilities
export const getChipColor = (format) => {
    return "#4CAF50";
};

export const getChipLabel = (format) => {
    if (!format) return "";

    if (format.startsWith("GRT")) {
        if (format === "GRT-WTA") return "GRT-WTA";
        if (format === "GRT-Top3") return "GRT-Top3";
        if (format === "GRT-Top5") return "GRT-Top5";
        if (format === "GRT-Top10") return "GRT-Top10";
        return "GRT";
    }
    if (format.startsWith("Top")) {
        if (format === "Top2-80-20") return "Top2";
        if (format === "Top2-70-30") return "Top2";
        if (format === "Top3") return "Top3";
        return "Top";
    }
    if (format === "WTA") return "WTA";
    if (format === "50/50") return "50/50";
    if (format.includes("x")) return format;
    return format;
};

export const getPayoutDescription = (format) => {
    if (!format) return "";

    switch (format) {
        case "2x":
            return "2x (45%) min 3";
        case "3x":
            return "3x (30%) min 4";
        case "4x":
            return "4x (22.5%) min 5";
        case "5x":
            return "5x (18%) min 6";
        case "10x":
            return "10x (9%) min 12";
        case "20x":
            return "20x (4.5%) min 24";
        case "WTA":
            return "WTA: 1-100% min 2";
        case "Top2-80-20":
            return "Top 2: 1-80%, 2-20% min 8";
        case "Top2-70-30":
            return "Top 2: 1-70%, 2-30% min 8";
        case "Top3":
            return "Top 3: 1-65%, 2-30%, 3-15% min 8";
        case "50/50":
            return "50/50: 50% win equal share min 4";
        case "GRT-WTA":
            return "GRT: Guarantee WTA \"X\" coins";
        case "GRT-Top3":
            return "GRT: Guarantee Top-3 \"X\" coins";
        case "GRT-Top5":
            return "GRT: Guarantee Top-5 \"X\" coins";
        case "GRT-Top10":
            return "GRT: Guarantee Top-10 \"X\" coins";
        default:
            return format;
    }
};

// Guarantee Settings Utilities
export const getGuaranteeInputCount = (format) => {
    switch (format) {
        case "GRT-WTA":
            return 1;
        case "GRT-Top3":
            return 3;
        case "GRT-Top5":
            return 5;
        case "GRT-Top10":
            return 10;
        default:
            return 1;
    }
};

export const getGuaranteeTitle = (format) => {
    switch (format) {
        case "GRT-WTA":
            return 'Guarantee WTA "X" Coins';
        case "GRT-Top3":
            return 'Guarantee Top-3 "X" Coins';
        case "GRT-Top5":
            return 'Guarantee Top-5 "X" Coins';
        case "GRT-Top10":
            return 'Guarantee Top-10 "X" Coins';
        default:
            return "Guarantee Settings";
    }
};

export const getGuaranteeDescription = (format, guaranteeValues) => {
    const inputCount = getGuaranteeInputCount(format);
    const baseDescription = getPayoutDescription(format);

    // If we have guarantee values set, show them
    if (guaranteeValues) {
        const values = [];
        for (let i = 1; i <= inputCount; i++) {
            const key = `${format}_${i}`;
            const value = guaranteeValues[key];
            if (value) {
                values.push(`${i}:${value}`);
            }
        }
        if (values.length === inputCount) {
            return `${baseDescription} (${values.join(", ")})`;
        }
    }

    return baseDescription;
};

// Payout Format Rendering
export const renderPayoutFormat = (payoutFormat, guaranteeValues, hasEmptyState = false) => {
    if (!payoutFormat) {
        return hasEmptyState ? (
            <Typography variant="body2" color="text.disabled">
                Select a payout format
            </Typography>
        ) : null;
    }

    const description = payoutFormat.startsWith("GRT-")
        ? getGuaranteeDescription(payoutFormat, guaranteeValues)
        : getPayoutDescription(payoutFormat);

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
                label={getChipLabel(payoutFormat)}
                size="small"
                sx={{
                    backgroundColor: getChipColor(payoutFormat),
                    color: "white",
                    fontWeight: 500,
                }}
            />
            <Typography variant="body2" color="text.secondary">
                {description}
            </Typography>
        </Box>
    );
};

// Available Payout Formats
export const PAYOUT_FORMATS = [
    "2x",
    "3x",
    "4x",
    "5x",
    "10x",
    "20x",
    "WTA",
    "Top2-80-20",
    "Top2-70-30",
    "Top3",
    "50/50",
    "GRT-WTA",
    "GRT-Top3",
    "GRT-Top5",
    "GRT-Top10",
];

// File Upload Utilities
export const handleFileUpload = async (type, event, setFormData) => {
    const file = event.target.files[0];
    if (!file) return;

    // File validation
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (!validTypes.includes(file.type)) {
        alert("Only JPG, JPEG, PNG, and WEBP formats are allowed.");
        return;
    }

    if (file.size > maxSize) {
        alert("File size should not exceed 2MB.");
        return;
    }

    // Upload to Azure
    try {
        const newFileName = file.name.replace(/\s+/g, "_");
        const renamedFile = new File([file], newFileName, {
            type: file.type,
        });

        await createBlobInContainer(renamedFile);

        const containerName = process.env.REACT_APP_STORAGE_CONTAINER;
        const storageAccountName = process.env.REACT_APP_STORAGERESOURCENAME;
        const imageUrl = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${newFileName}`;

        // Update form data with Azure URL
        if (type === "icon") {
            setFormData((prev) => ({ ...prev, iconUrl: imageUrl }));
        } else if (type === "banner1" || type === "banner") {
            setFormData((prev) => ({ ...prev, bannerUrl: imageUrl }));
        }

        console.log(`${type} uploaded successfully:`, imageUrl);
        return imageUrl;
    } catch (error) {
        console.error(`Error uploading ${type}:`, error);
        alert(`Error uploading ${type}. Please try again.`);
        throw error;
    }
};

// Guarantee Settings Handlers
export const createGuaranteeHandlers = (
    formData,
    setFormData,
    guaranteeValues,
    setGuaranteeValues,
    setGuaranteeSettingsOpen
) => {
    const handleGuaranteeSettingsSave = () => {
        const format = formData.tempPayoutFormat;
        const inputCount = getGuaranteeInputCount(format);

        // Validate that all inputs have values
        const hasAllValues = Array.from(
            { length: inputCount },
            (_, i) => i + 1
        ).every(
            (position) =>
                guaranteeValues[`${format}_${position}`] &&
                guaranteeValues[`${format}_${position}`] > 0
        );

        if (!hasAllValues) {
            alert("Please fill in all guarantee coin values.");
            return;
        }

        // Save the payout format and guarantee values
        setFormData((prev) => ({
            ...prev,
            payoutFormat: format,
            guaranteeValues: { ...prev.guaranteeValues, ...guaranteeValues },
            tempPayoutFormat: undefined,
        }));

        setGuaranteeSettingsOpen(false);
    };

    const handleGuaranteeSettingsCancel = () => {
        setGuaranteeSettingsOpen(false);
        setFormData((prev) => ({ ...prev, tempPayoutFormat: undefined }));
    };

    const handleGuaranteeValueChange = (key, value) => {
        setGuaranteeValues((prev) => ({
            ...prev,
            [key]: parseInt(value) || 0,
        }));
    };

    return {
        handleGuaranteeSettingsSave,
        handleGuaranteeSettingsCancel,
        handleGuaranteeValueChange,
    };
};

// Payout Format Change Handler
export const createPayoutFormatChangeHandler = (
    formData,
    setFormData,
    setGuaranteeValues,
    setGuaranteeSettingsOpen,
    canEditGuarantee = true
) => {
    return (value) => {
        if (!value) return;

        // Check if it's a guarantee format
        if (value.startsWith("GRT-")) {
            if (canEditGuarantee) {
                // Pre-populate with existing values if available
                const inputCount = getGuaranteeInputCount(value);
                const existingValues = {};
                for (let i = 1; i <= inputCount; i++) {
                    const key = `${value}_${i}`;
                    if (formData.guaranteeValues && formData.guaranteeValues[key]) {
                        existingValues[key] = formData.guaranteeValues[key];
                    }
                }
                setGuaranteeValues(existingValues);

                setGuaranteeSettingsOpen(true);
                // Set the selected format but don't update formData yet
                setFormData((prev) => ({ ...prev, tempPayoutFormat: value }));
            } else {
                // Just update the payout format without opening modal
                setFormData((prev) => ({ ...prev, payoutFormat: value }));
            }
        } else {
            // Regular format, update normally
            setFormData((prev) => ({ ...prev, payoutFormat: value }));
        }
    };
};

// Challenge Type Utilities
export const getChallengeTypeLabel = (type) => {
    return type === 1 ? "Public Challenge" : "Club Challenge";
};

export const getChallengeTypeChip = (type) => {
    const isPublic = type === 1;
    return (
        <Chip
            label={isPublic ? "Public" : "Club"}
            size="small"
            sx={{
                backgroundColor: isPublic ? "#ff9800" : "#2196f3",
                color: "white",
                fontWeight: 500,
            }}
        />
    );
};

// Validation Utilities
export const validateChallengeForm = (formData) => {
    const errors = [];

    if (!formData.challengeName?.trim() && !formData.clubName?.trim()) {
        errors.push("Challenge name is required");
    }

    if (formData.entryFee < 0 || formData.entryFee > 99999) {
        errors.push("Entry fee must be between 0 and 99999");
    }

    if (formData.minXpLevel < 0 || formData.minXpLevel > 17) {
        errors.push("Min XP Level must be between 0 and 17");
    }

    return errors;
};